// CanvasPendingApptBanner — Ship 6 quick win #6 (May 2, 2026)
//
// Surfaces a banner at the top of DealDetail Overview when a canvasser-
// submitted appointment is sitting in Alex's queue waiting for approval.
// Shows: who submitted, how long ago, GPS distance from pin, "View in queue".
// Disappears once Alex approves/rejects.
//
// We resolve the pending row by deal_id when Alex's approval has linked it,
// AND by knock_pin_id → deal_id when the linkage is set on the deals row.
// In practice, pending rows pre-approval don't have deal_id set — they
// only get it AFTER approval, which is when this banner stops showing.
// So the *useful* lookup is via knock_pin_id → match deals.knock_pin_id.

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";

interface PendingRow {
  id: string;
  knock_pin_id: string | null;
  submitted_at: string;
  submitted_by: string;
  homeowner_first: string;
  homeowner_last: string;
  appointment_at: string;
  status: string;
  submitter_lat: number | null;
  submitter_lon: number | null;
  pin?: { lat: number | null; lon: number | null } | null;
  submitter?: { full_name: string } | null;
}

function distanceFeet(
  aLat: number | null | undefined, aLon: number | null | undefined,
  bLat: number | null | undefined, bLon: number | null | undefined,
): number | null {
  if (aLat == null || aLon == null || bLat == null || bLon == null) return null;
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const lat1 = toRad(aLat); const lat2 = toRad(bLat);
  const sa = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(sa))) * 3.28084;
}

function distanceChip(ft: number | null) {
  if (ft == null) return null;
  if (ft <= 50)  return { emoji: "🟢", label: "On property", cls: "bg-emerald-100 text-emerald-800" };
  if (ft <= 300) return { emoji: "🟡", label: `${Math.round(ft)} ft from pin`, cls: "bg-amber-100 text-amber-800" };
  return { emoji: "🔴", label: ft <= 5280 ? `${Math.round(ft)} ft from pin` : `${(ft/5280).toFixed(1)} mi from pin`, cls: "bg-red-100 text-red-800" };
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

export default function CanvasPendingApptBanner({
  dealId,
  knockPinId,
}: {
  dealId: string;
  knockPinId: string | null;
}) {
  const [row, setRow] = useState<PendingRow | null>(null);

  useEffect(() => {
    if (!knockPinId) { setRow(null); return; }
    let cancelled = false;
    async function load() {
      // Pending rows don't get deal_id stamped until approval, so we look up
      // by the deal's knock_pin_id and only show pending rows.
      const { data, error } = await supabase
        .from("pending_canvas_appointments")
        .select("id, knock_pin_id, submitted_at, submitted_by, homeowner_first, homeowner_last, appointment_at, status, submitter_lat, submitter_lon, submitter:team_roster!pending_canvas_appointments_submitted_by_fkey(full_name), pin:knock_pins!pending_canvas_appointments_knock_pin_id_fkey(lat, lon)")
        .eq("knock_pin_id", knockPinId!)
        .eq("status", "pending")
        .order("submitted_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error) return;
      setRow((data ?? null) as PendingRow | null);
    }
    load();
    // Realtime — banner disappears the moment Alex approves/rejects
    const ch = supabase
      .channel(`pending-appt-banner:${knockPinId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "pending_canvas_appointments", filter: `knock_pin_id=eq.${knockPinId}` }, () => load())
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(ch); };
  }, [knockPinId, dealId]);

  if (!row) return null;

  const ft = distanceFeet(row.submitter_lat, row.submitter_lon, row.pin?.lat ?? null, row.pin?.lon ?? null);
  const chip = distanceChip(ft);

  return (
    <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-3 shadow-sm">
      <span className="mt-0.5 inline-block h-2 w-2 animate-pulse rounded-full bg-orange-500" />
      <div className="flex-1 text-sm">
        <div className="font-semibold text-orange-900">
          Appointment submitted to Alex · {relativeTime(row.submitted_at)}
        </div>
        <div className="mt-0.5 text-xs text-orange-800">
          {row.submitter?.full_name ?? "Canvasser"} captured this appointment at the door
          {chip ? (
            <> (<span className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${chip.cls}`}>{chip.emoji} {chip.label}</span>)</>
          ) : null}
          . Awaiting approval — once Alex confirms, the deal will move into the pipeline.
        </div>
      </div>
      <a
        href="/canvas/approvals"
        className="self-center rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-orange-600"
      >
        View in queue →
      </a>
    </div>
  );
}
